import { createRoute, Outlet } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { ProDriverRecordScreen } from "@/app/ProDrivers/Drivers/Screens/ProDriverRecordScreen";
import { ProDriverTableScreen } from "@/app/ProDrivers/Drivers/Screens/ProDriverTableScreen";
import { ProDriverDetailScreen } from "@/app/ProDrivers/Drivers/Screens/ProDriverDetailScreen";
import { appLayoutRoute } from "@/routes/layout-routes";
import { z } from "zod";
import {
  fetchQuery,
  prefetchInfiniteQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { proDriverListQuery } from "@/app/ProDrivers/Drivers/GraphQL/proDriverListQuery";
import { getProDriverQueryVariables } from "@/app/ProDrivers/Drivers/Utils/getProDriverQueryVariables";
import { proDriverRecordQuery } from "@/app/ProDrivers/Drivers/GraphQL/proDriverRecordQuery";
import { ProDriverLayout } from "@/app/ProDrivers/Common/ProDriverLayout";
import { WorkOrdersTableScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrdersTableScreen";
import {
  ModelType,
  QueryWorkOrderJobOpeningsOrderByColumn,
  SortOrder,
  ThreadSort,
  WorkOrderAssignmentStatus,
  WorkOrderStatus,
  QueryThreadMessagesOrderByColumn,
} from "@/gql/graphql";
import {
  workOrderListQuery,
  workOrderRecordQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { workOrderJobOpeningListQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningQuery";
import { WorkOrderBillingScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderBillingScreen";
import { WorkOrderDriversScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderDriversScreen";
import { WorkOrderDetailsScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderDetailsScreen";
import { WorkOrderActivityScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderActivityScreen";
import { createQueryVariables } from "@/lib/Components/DataTable/utils/data-table-utils";
import { getWorkOrderQueryVariables } from "@/app/ProDrivers/WorkOrders/utils/getWorkOrderQueryVariables";
import { AssignmentsTableScreen } from "@/app/ProDrivers/Assignments/Screens/AssignmentsTableScreen";
import { AssignmentDrawerRoute } from "@/app/ProDrivers/Assignments/Components/AssignmentDrawerRoute";
import { WorkOrderMessagesScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderMessagesScreen";
import { WorkOrderMessageThreadScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderMessageThreadScreen";
import { WorkOrderMessagesEmptyScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderMessagesEmptyScreen";
import { workOrderAssignmentListQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentQuery";
import {
  threadRecordQuery,
  threadsInfiniteList,
} from "@/app/Threads/GraphQL/threadQueries";
import { threadMessagesQuery } from "@/app/Threads/GraphQL/threadMessageQueries";
import { threadSearchParams } from "@/app/Threads/utils/threadSearchParams";
import { WorkOrderRecordLayout } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderRecordLayout";
import { WorkOrderListScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderListScreen";
import { getWorkOrderListQueryVariables } from "@/app/ProDrivers/WorkOrders/utils/getWorkOrderListQueryVariables";
import { WorkOrderRecordScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderRecordScreen";
import { ProDriverThreadsScreen } from "@/app/ProDrivers/Threads/ProDriverThreadsScreen";
import { ProDriverThreadsEmptyScreen } from "@/app/ProDrivers/Threads/ProDriverThreadsEmptyScreen";
import { ProDriverThreadScreen } from "@/app/ProDrivers/Threads/ProDriverThreadScreen";
import { ProDriverTasksScreen } from "@/app/ProDrivers/Tasks/ProDriverTasksScreen";
import { WorkOrderIncidentsScreen } from "@/app/ProDrivers/WorkOrders/Screens/WorkOrderIncidentsScreen";
import { ProDriverIncidentsScreen } from "@/app/ProDrivers/Drivers/Screens/ProDriverIncidentsScreen";

const proDriverSearchParams = z.object({
  proDriverSearch: z.string().optional(),
});
export type ProDriverListSearchParams = z.infer<typeof proDriverSearchParams>;

export const proDriversLayout = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/pro-drivers",
  component: ProDriverLayout,
  head: () => ({
    meta: [
      {
        title: "Pro Drivers - Imoova Admin",
      },
    ],
  }),
});

export const proDriverThreadsRoute = createRoute({
  getParentRoute: () => proDriversLayout,
  path: "/messages",
  component: ProDriverThreadsScreen,
  validateSearch: zodValidator(threadSearchParams),
  head: () => ({
    meta: [
      {
        title: "Pro Driver Messages - Imoova Admin",
      },
    ],
  }),
});

export const proDriverThreadsIndexRoute = createRoute({
  getParentRoute: () => proDriverThreadsRoute,
  path: "/",
  component: ProDriverThreadsEmptyScreen,
});

export const proDriverThreadRoute = createRoute({
  getParentRoute: () => proDriverThreadsRoute,
  path: "$threadId",
  component: ProDriverThreadScreen,
  loader: ({ params }) => {
    return Promise.all([
      fetchQuery(threadRecordQuery, { id: params.threadId }, params.orgId),
      prefetchInfiniteQuery(
        threadMessagesQuery,
        {
          first: 20,
          thread_id: params.threadId,
          orderBy: [
            {
              column: QueryThreadMessagesOrderByColumn.CreatedAt,
              order: SortOrder.Desc,
            },
          ],
        },
        params.orgId,
      ),
    ]);
  },
});

export const proDriverTasksRoute = createRoute({
  getParentRoute: () => proDriversLayout,
  path: "/tasks",
  component: ProDriverTasksScreen,
  head: () => ({
    meta: [
      {
        title: "Pro Driver Tasks - Imoova Admin",
      },
    ],
  }),
});

export const proDriverTableRoute = createRoute({
  getParentRoute: () => proDriversLayout,
  path: "/drivers",
  validateSearch: zodValidator(proDriverSearchParams),
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    return prefetchInfiniteQuery(
      proDriverListQuery,
      getProDriverQueryVariables(deps),
      params.orgId,
    );
  },
  component: ProDriverTableScreen,
  head: () => ({
    meta: [
      {
        title: "Pro Drivers - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage pro drivers",
      },
    ],
  }),
});

export const proDriverRoute = createRoute({
  getParentRoute: () => proDriverTableRoute,
  path: "$id",
  component: ProDriverRecordScreen,
  loader: ({ params }) => {
    return fetchQuery(proDriverRecordQuery, { id: params.id }, params.orgId);
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.proDriver?.name ?? "--"} - Imoova Admin`,
      },
    ],
  }),
});

export const proDriverDetailRoute = createRoute({
  getParentRoute: () => proDriverRoute,
  path: "/",
  component: ProDriverDetailScreen,
});

export const proDriverIncidentsRoute = createRoute({
  getParentRoute: () => proDriverRoute,
  path: "/incidents",
  component: ProDriverIncidentsScreen,
});

const validWorkOrderStatuses = Object.values(WorkOrderStatus);

const proDriverWorkOrderSearchParams = z.object({
  workOrderStatus: z
    .array(z.string())
    .optional()
    .transform((statuses) =>
      statuses?.filter((s): s is WorkOrderStatus =>
        validWorkOrderStatuses.includes(s as WorkOrderStatus),
      ),
    ),
});

export const workOrdersRoute = createRoute({
  getParentRoute: () => proDriversLayout,
  path: "/work-orders",
  component: Outlet,
  validateSearch: zodValidator(proDriverWorkOrderSearchParams),
  head: () => ({
    meta: [
      {
        title: "Work Orders - Imoova Admin",
      },
    ],
  }),
});

export const workOrderTableRoute = createRoute({
  getParentRoute: () => workOrdersRoute,
  path: "/",
  component: WorkOrdersTableScreen,
  validateSearch: zodValidator(proDriverWorkOrderSearchParams),
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const queryVariables = createQueryVariables(
      "work-order-table",
      getWorkOrderQueryVariables({
        supplierId: null,
        status: deps.workOrderStatus ?? [],
      }),
    );

    return fetchQuery(workOrderListQuery, queryVariables, params.orgId);
  },
  head: () => ({
    meta: [
      {
        title: "Work Orders - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage work orders",
      },
    ],
  }),
});

const workOrderRecordSearchParams = z.object({
  assignmentId: z.string().optional(),
  quoteId: z.string().optional(),
  invoiceId: z.string().optional(),
  card: z.string().optional(),
});

export const workOrderRecordSidebarLayout = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: "_workOrderRecordSidebar",
  component: WorkOrderListScreen,
  loader: ({ params }) => {
    return prefetchInfiniteQuery(
      workOrderListQuery,
      getWorkOrderListQueryVariables({}),
      params.orgId,
    );
  },
});

export const workOrderRecordLayoutRoute = createRoute({
  getParentRoute: () => workOrderRecordSidebarLayout,
  path: "/pro-drivers/work-orders/$id",
  component: WorkOrderRecordLayout,
  validateSearch: zodValidator(workOrderRecordSearchParams),
  loader: ({ params }) => {
    return fetchQuery(
      workOrderRecordQuery,
      {
        id: params.id,
      },
      params.orgId,
    );
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.workOrder?.reference ?? ""} - Imoova Admin`,
      },
    ],
  }),
});

export const workOrderRecordRoute = createRoute({
  getParentRoute: () => workOrderRecordLayoutRoute,
  id: "work-order-record",
  component: WorkOrderRecordScreen,
});

export const workOrderDetailsRoute = createRoute({
  getParentRoute: () => workOrderRecordRoute,
  path: "/",
  component: WorkOrderDetailsScreen,
});

export const workOrderBillingRoute = createRoute({
  getParentRoute: () => workOrderRecordRoute,
  path: "/billing",
  component: WorkOrderBillingScreen,
});

export const workOrderDriversRoute = createRoute({
  getParentRoute: () => workOrderRecordRoute,
  path: "/job-openings",
  component: WorkOrderDriversScreen,
  loader: ({ params }) => {
    return fetchQuery(
      workOrderJobOpeningListQuery,
      {
        work_order_id: params.id,
        first: 20,
        orderBy: [
          {
            column: QueryWorkOrderJobOpeningsOrderByColumn.CreatedAt,
            order: SortOrder.Asc,
          },
        ],
      },
      params.orgId,
    );
  },
});

export const workOrderActivityRoute = createRoute({
  getParentRoute: () => workOrderRecordRoute,
  path: "/activity",
  component: WorkOrderActivityScreen,
});

export const workOrderIncidentsRoute = createRoute({
  getParentRoute: () => workOrderRecordRoute,
  path: "/incidents",
  component: WorkOrderIncidentsScreen,
});

export const workOrderMessagesRoute = createRoute({
  getParentRoute: () => workOrderRecordRoute,
  path: "/messages",
  component: WorkOrderMessagesScreen,
  validateSearch: zodValidator(threadSearchParams),
  loaderDeps: ({ search }) => ({ threadSort: search.threadSort }),
  loader: ({ params, deps }) => {
    return prefetchInfiniteQuery(
      threadsInfiniteList,
      {
        first: 20,
        threadable_type: ModelType.WorkOrder,
        threadable_id: params.id,
        sort: deps.threadSort ?? ThreadSort.LatestMessage,
      },
      params.orgId,
    );
  },
});

export const workOrderMessagesIndexRoute = createRoute({
  getParentRoute: () => workOrderMessagesRoute,
  path: "/",
  component: WorkOrderMessagesEmptyScreen,
});

export const workOrderMessageThreadRoute = createRoute({
  getParentRoute: () => workOrderMessagesRoute,
  path: "$threadId",
  component: WorkOrderMessageThreadScreen,
});

const validAssignmentStatuses = Object.values(WorkOrderAssignmentStatus);

const assignmentSearchParams = z.object({
  assignmentStatus: z
    .array(z.string())
    .optional()
    .transform((statuses) =>
      statuses?.filter((s): s is WorkOrderAssignmentStatus =>
        validAssignmentStatuses.includes(s as WorkOrderAssignmentStatus),
      ),
    ),
  filterSupplierId: z.string().optional(),
});

const defaultAssignmentStatuses = [
  WorkOrderAssignmentStatus.Assigned,
  WorkOrderAssignmentStatus.OnRoad,
  WorkOrderAssignmentStatus.Delivered,
  WorkOrderAssignmentStatus.ReceiptsUploaded,
];

export const assignmentsRoute = createRoute({
  getParentRoute: () => proDriversLayout,
  path: "/assignments",
  component: AssignmentsTableScreen,
  validateSearch: zodValidator(assignmentSearchParams),
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    return fetchQuery(
      workOrderAssignmentListQuery,
      {
        page: 1,
        first: 20,
        status: deps.assignmentStatus ?? defaultAssignmentStatuses,
      },
      params.orgId,
    );
  },
  head: () => ({
    meta: [
      {
        title: "Assignments - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage driver assignments",
      },
    ],
  }),
});

export const assignmentRecordRoute = createRoute({
  getParentRoute: () => assignmentsRoute,
  path: "$assignmentId",
  component: AssignmentDrawerRoute,
});
